import React, { useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import { Box } from '@mui/material';
import {
  motion,
  useInView,
  useMotionValue,
  useSpring,
  useReducedMotion,
} from 'framer-motion';

// ─────────────────────────────────────────────────────────────────────────────
// Shared motion kit for the modern redesign. Built on framer-motion (already a
// dependency). All helpers respect `prefers-reduced-motion`.
// ─────────────────────────────────────────────────────────────────────────────

const EASE = [0.22, 1, 0.36, 1];

/**
 * Stagger — a container that reveals its <StaggerItem> children one after
 * another when it scrolls into view.
 */
export const Stagger = ({ children, delay = 0, gap = 0.09, once = true, ...rest }) => (
  <motion.div
    initial='hidden'
    whileInView='show'
    viewport={{ once, margin: '-60px' }}
    variants={{
      hidden: {},
      show: { transition: { staggerChildren: gap, delayChildren: delay } },
    }}
    {...rest}
  >
    {children}
  </motion.div>
);

Stagger.propTypes = {
  children: PropTypes.node,
  delay: PropTypes.number,
  gap: PropTypes.number,
  once: PropTypes.bool,
};

/** StaggerItem — a single fade/lift child driven by its <Stagger> parent. */
export const StaggerItem = ({ children, y = 26, ...rest }) => (
  <motion.div
    variants={{
      hidden: { opacity: 0, y },
      show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
    }}
    {...rest}
  >
    {children}
  </motion.div>
);

StaggerItem.propTypes = { children: PropTypes.node, y: PropTypes.number };

/**
 * HoverLift — wraps interactive content so it lifts on hover and presses on tap.
 * Use `style={{ height: '100%' }}` when wrapping equal-height grid cards.
 */
export const HoverLift = ({ children, lift = -6, scale = 1, ...rest }) => (
  <motion.div
    whileHover={{ y: lift, scale }}
    whileTap={{ scale: 0.99 }}
    transition={{ duration: 0.3, ease: EASE }}
    {...rest}
  >
    {children}
  </motion.div>
);

HoverLift.propTypes = {
  children: PropTypes.node,
  lift: PropTypes.number,
  scale: PropTypes.number,
};

/** PageFade — subtle fade/rise wrapper for route-level content. */
export const PageFade = ({ children, ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, ease: EASE }}
    {...rest}
  >
    {children}
  </motion.div>
);

PageFade.propTypes = { children: PropTypes.node };

/**
 * Counter — animates a number from 0 → `value` when scrolled into view.
 * Renders inside a <Box component="span"> so it can sit inline in headings.
 */
export const Counter = ({
  value,
  duration = 1.6,
  prefix = '',
  suffix = '',
  decimals = 0,
  ...rest
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const reduce = useReducedMotion();
  const mv = useMotionValue(0);
  const spring = useSpring(mv, {
    duration: duration * 1000,
    bounce: 0,
  });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (inView) mv.set(reduce ? value : value);
    if (reduce) setDisplay(value);
  }, [inView, value, mv, reduce]);

  useEffect(() => {
    if (reduce) return undefined;
    const unsub = spring.on('change', v => setDisplay(v));
    return () => unsub();
  }, [spring, reduce]);

  return (
    <Box component='span' ref={ref} {...rest}>
      {prefix}
      {Number(display).toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}
      {suffix}
    </Box>
  );
};

Counter.propTypes = {
  value: PropTypes.number.isRequired,
  duration: PropTypes.number,
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  decimals: PropTypes.number,
};

export { motion };
